import { useSyncExternalStore } from 'react';

import { ottoNowSchema, type OttoNowData } from '../lib/bureau/ottoNow';
import { logWarn } from '../lib/logger';
import { onMissionsChanged } from '../lib/mission/missionsChanged';
import { logReadFailure } from '../lib/net/logReadFailure';
import { startPeriodicRead } from '../lib/net/periodicRead';
import { supabase } from '../lib/supabase';

/** How often what Otto does is reread, while the page is awake. */
const POLL_MS = 30_000;

type RpcCall = (fn: 'get_otto_now') => Promise<{ data: unknown; error: { message: string; code?: string } | null }>;

let data: OttoNowData | null = null;
const listeners = new Set<() => void>();
let stop: (() => void) | null = null;
let inFlight: Promise<void> | null = null;
let queued = false;

function emit(next: OttoNowData) {
  data = next;
  for (const listener of listeners) listener();
}

async function readOnce() {
  // `get_otto_now` is newer than the generated Supabase types: the call is typed here, its answer by Zod.
  const { data: raw, error } = await (supabase.rpc as unknown as RpcCall)('get_otto_now');
  if (error) {
    logReadFailure('travail', 'get_otto_now unreadable', error);
    return;
  }
  const parsed = ottoNowSchema.safeParse(raw);
  if (parsed.success) emit(parsed.data);
  else logWarn('travail', 'get_otto_now answer has an unexpected shape', { issues: parsed.error.issues.length });
}

/** One read at a time for every surface; one asked meanwhile (a mission moved) runs right after. */
function read(): Promise<void> {
  if (inFlight) {
    queued = true;
    return inFlight;
  }
  inFlight = readOnce()
    .catch((err: unknown) => logReadFailure('travail', 'get_otto_now unreadable', err))
    .finally(() => {
      inFlight = null;
      if (queued) {
        queued = false;
        void read();
      }
    });
  return inFlight;
}

function start(): () => void {
  void read();
  const stopPolling = startPeriodicRead({ everyMs: POLL_MS, read: () => void read() });
  const offMissions = onMissionsChanged(() => void read());
  return () => {
    stopPolling();
    offMissions();
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!stop) stop = start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && stop) {
      stop();
      stop = null;
    }
  };
}

/**
 * What Otto does now (`get_otto_now()`, PLAN-BUREAU lot 4) — once for the whole app: the small Otto
 * of the header and the Bureau's sidebar share one reader (like `useWaitingSignal`), reread every
 * 30 s while the page is awake, at its wake, and as soon as a mission changes. A failed read keeps
 * the last answer on screen (and is traced); before any answer, there is nothing to show.
 */
export function useOttoNow(): OttoNowData | null {
  return useSyncExternalStore(subscribe, () => data, () => data);
}

/** Test seam: forget the shared answer between tests. */
export function resetOttoNowForTests(): void {
  stop?.();
  stop = null;
  listeners.clear();
  data = null;
  inFlight = null;
  queued = false;
}
